import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Dropdown,
  Checkbox,
  Input,
} from "antd";
import React, { useCallback, useMemo, useState } from "react";
import {
  PrinterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import XLSX from "xlsx-js-style";
import {
  HEADERS_EXPORT_BEN,
  HEADERS_EXPORT_DETAIL,
  POST_LOAN_DB2_V2,
  POST_REPORT_SELLING_AND_INSTALLMENTS,
} from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import "../../../assets/font/THSarabunNew-normal";
import "../../../assets/font/THSarabunNew-bold";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";
import ThaiDatePicker from "../../../hook/ThaiDatePicker";

const TwoLineHeader = ({ top, bottom }) => (
  <div style={{ textAlign: "center", lineHeight: 1.4 }}>
    <div>{top}</div>
    <div style={{ fontSize: 11, fontWeight: 400, color: "#888" }}>{bottom}</div>
  </div>
);

const Main = () => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const [signOut] = TokenCheck();
  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [isFlagAgreement, setIsFlagAgreement] = useState(true);
  const [isFlagEnforce, setIsFlagEnforce] = useState(true);
  const [searchContno, setSearchContno] = useState("");
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));

  const fmtPos = (val) => (parseFloat(val) > 0 ? currencyFormatPoint(val) : "");

  // ── Query ─────────────────────────────────────────────
  const onQuery = () => {
    if (!startDate || !endDate) {
      message.warning("กรุณาเลือกวันที่ก่อนค้นหา");
      return;
    }
    queryData({
      firstDate: dayjs(startDate).format("YYYY-MM-DDTHH:mm:ssZ"),
      lastDate: dayjs(endDate).format("YYYY-MM-DDTHH:mm:ssZ"),
    });
  };

  const queryData = async (payload) => {
    setLoading(true);
    try {
      const res = await axios.post(
        POST_REPORT_SELLING_AND_INSTALLMENTS,
        payload,
        {
          headers: HEADERS_EXPORT_DETAIL,
        },
      );
      if (res.status !== 200) {
        setArrayPay([]);
        return;
      }

      const reportData = res.data?.data ?? [];

      if (!reportData.length) {
        setArrayPay([]);
        return;
      }

      const groupedBySchema = reportData.reduce((acc, item) => {
        if (!item.CONTNO || !item.SCHEMA) return acc;
        if (!acc[item.SCHEMA]) acc[item.SCHEMA] = [];
        acc[item.SCHEMA].push(`'${item.CONTNO}'`);
        return acc;
      }, {});

      const schemaPayload = Object.entries(groupedBySchema).reduce(
        (acc, [schema, arr]) => {
          acc[schema] = arr.join(",");
          return acc;
        },
        {},
      );

      let db2Map = {};
      try {
        const db2Res = await axios.post(POST_LOAN_DB2_V2, schemaPayload, {
          headers: HEADERS_EXPORT_BEN,
        });
        const db2Data = Array.isArray(db2Res.data)
          ? db2Res.data
          : Array.isArray(db2Res.data?.data)
            ? db2Res.data.data
            : [];

        db2Data.forEach((item) => {
          const contno = item?.LOAN?.CONTNO;
          if (!contno) return;
          db2Map[contno] = {
            LOCAT: item.LOAN?.LOCAT,
            PRODUCTLOAN: item.LOAN?.PRODUCTLOAN,
            ISSUNO: item.LOAN?.ISSUNO,
            TYPE: item.MORTGAGE?.TYPE,
            MODEL: item.MORTGAGE?.MODEL,
            BAAB: item.MORTGAGE?.BAAB,
            COLOR: item.MORTGAGE?.COLOR,
            REGNO: item.MORTGAGE?.REGNO,
            ENGNO: item.MORTGAGE?.ENGNO,
            MANUYR: item.MORTGAGE?.MANUYR,
            DORECV: item.MORTGAGE?.DORECV,
            OFFIC: item.CUSTOMER?.OFFIC,
            OCCUP: item.CUSTOMER?.OCCUP,
            ADDRESS: item.CUSTOMER?.ADDRESS ?? [],
            GUARANTORS: item.GUARANTORS ?? [],
          };
        });
      } catch (e) {
        console.warn("DB2 failed:", e);
      }

      setArrayPay(
        reportData
          .map((item) => ({ ...item, ...(db2Map[item.CONTNO] ?? {}) }))
          .sort((a, b) => dayjs(a.SDATE).valueOf() - dayjs(b.SDATE).valueOf()),
      );
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
      if (err?.response?.status === 401) {
        signOut();
      }
      setArrayPay([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterData = useCallback(() => {
    if (!arrayPay.length) return [];

    const keyword = searchContno.trim().toLowerCase();

    return arrayPay.filter((item) => {
      // ✅ ตัด closeContno = 1 ออกเสมอ
      // if (item.closeContno === 1) return false;

      // ✅ ค้นหาจากเลขสัญญา (เดิม/ใหม่)
      if (keyword) {
        const contno = (item.CONTNO || "").toLowerCase();
        const newContno = (item.newContno || "").toLowerCase();
        if (!contno.includes(keyword) && !newContno.includes(keyword)) {
          return false;
        }
      }

      // ✅ ทั้งคู่ true → เอาทั้งหมด
      if (isFlagAgreement && isFlagEnforce) return true;

      // ✅ Agreement อย่างเดียว → flagLoanNew = 1
      if (isFlagAgreement && !isFlagEnforce) return item.flagLoanNew === 1;

      // ✅ Enforce อย่างเดียว → flagLoanNew = 2
      if (!isFlagAgreement && isFlagEnforce) return item.flagLoanNew === 2;

      // ✅ ทั้งคู่ false → ไม่มีข้อมูล
      return false;
    });
  }, [arrayPay, isFlagAgreement, isFlagEnforce, searchContno]);

  // ── Columns ───────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "สาขา",
      align: "center",
      width: 60,
      render: (_, r) => "MIT",
    },
    {
      title: "วันที่ขาย",
      align: "center",
      width: 90,
      sorter: (a, b) => dayjs(a.SDATE).valueOf() - dayjs(b.SDATE).valueOf(),
      render: (_, r) => (
        <div>
          {r.SDATE ? dayjs(r.SDATE).add(543, "year").format("DD/MM/YYYY") : "-"}
        </div>
      ),
    },
    {
      title: <TwoLineHeader top="เลขสัญญา" bottom="(สถานะ)" />,
      key: "contract_model",
      align: "center",
      width: 130,
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600 }}>{r.newContno || "-"}</div>
          <div style={{ color: "#888", fontSize: 12 }}>
            {`(${
              r.flagLoanNew === 1 && r.LAW_TYPE_ID !== 6
                ? "ทำยอม"
                : r.flagLoanNew === 1 && r.LAW_TYPE_ID === 6
                  ? "ทำยอม(ฟ้องส่วนต่าง)"
                  : r.flagLoanNew === 2 && r.LAW_TYPE_ID === 6
                    ? "พิพากษา(ฟ้องส่วนต่าง)"
                    : "พิพากษา"
            })`.trim()}
          </div>
        </>
      ),
    },
    {
      title: "ชื่อลูกค้า",
      key: "name",
      align: "center",
      width: 140,
      render: (_, r) => (
        <>
          <div>
            {`${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim() || "-"}
          </div>
        </>
      ),
    },
    {
      title: "เลขตัวถัง",
      key: "STRNO",
      dataIndex: "STRNO",
      align: "center",
      width: 140,
    },
    {
      title: "ปีผลิต",
      key: "MANUYR",
      dataIndex: "MANUYR",
      align: "center",
      width: 140,
    },
    {
      title: "ทะเบียน",
      key: "REGNO",
      dataIndex: "REGNO",
      align: "center",
      width: 140,
    },
    {
      title: <TwoLineHeader top="งวดละ" bottom="จำนวนงวด" />,
      key: "TOT_UPAY",
      align: "center",
      width: 140,
      sorter: (a, b) => {
        const valA =
          a.flagLoanNew === 1 ? (a.TOT_UPAY ?? 0) : (a.totalJugde ?? 0);
        const valB =
          b.flagLoanNew === 1 ? (b.TOT_UPAY ?? 0) : (b.totalJugde ?? 0);
        return valA - valB;
      },
      render: (_, r) => (
        <>
          <div>
            {r.flagLoanNew === 1
              ? currencyFormatPoint(r.TOT_UPAY ?? 0)
              : currencyFormatPoint(r.totalJugde ?? 0)}
          </div>
          <div style={{ color: "#888", fontSize: 12 }}>
            {r.flagLoanNew === 1 ? r.T_NOPAY : "1"}
          </div>
        </>
      ),
    },
    {
      title: <TwoLineHeader top="ยอดที่ต้องชำระ" bottom="เปอร์เซ็นยอดขาย" />,
      key: "totalJugde",
      align: "center",
      width: 140,
      sorter: (a, b) => (a.totalJugde ?? 0) - (b.totalJugde ?? 0),
      render: (_, r) => (
        <>
          <div>
            {/* {r.flagLoanNew === 1
              ? currencyFormatPoint(r.totalJugde ?? 0)
              : currencyFormatPoint(parseFloat(r.totalJugde || 0))} */}
            {currencyFormatPoint(parseFloat(r.totalJugde || 0))}
          </div>
          <div style={{ color: "#888", fontSize: 12 }}>
            {r.creditPercen ?? 0}%
          </div>
        </>
      ),
    },
    {
      title: "ลูกหนี้",
      key: "creditDebtor",
      align: "center",
      width: 140,
      sorter: (a, b) => {
        const valA =
          a.flagLoanNew === 1
            ? (a.totalJugde ?? 0)
            : parseFloat(a.creditDebtor || 0);
        const valB =
          b.flagLoanNew === 1
            ? (b.totalJugde ?? 0)
            : parseFloat(b.creditDebtor || 0);
        return valA - valB;
      },
      render: (_, r) => (
        <>
          <div>
            {r.flagLoanNew === 1
              ? currencyFormatPoint(r.totalJugde ?? 0)
              : currencyFormatPoint(parseFloat(r.creditDebtor || 0))}
          </div>
        </>
      ),
    },
    {
      title: "ต้นทุน",
      key: "creditDebtor",
      dataIndex: "creditDebtor",
      align: "center",
      width: 140,
      sorter: (a, b) => (a.creditDebtor ?? 0) - (b.creditDebtor ?? 0),
      render: (_, r) => <div>{currencyFormatPoint(r.creditDebtor ?? 0)}</div>,
    },

    {
      title: "ดอกผล",
      align: "center",
      width: 70,
      sorter: (a, b) => {
        const valA =
          a.flagLoanNew === 1
            ? (parseFloat(a.totalJugde) || 0) - parseFloat(a.creditDebtor || 0)
            : 0;
        const valB =
          b.flagLoanNew === 1
            ? (parseFloat(b.totalJugde) || 0) - parseFloat(b.creditDebtor || 0)
            : 0;
        return valA - valB;
      },
      render: (val, r) => (
        <>
          {r.flagLoanNew === 1
            ? currencyFormatPoint(
                (parseFloat(r.totalJugde) || 0) -
                  parseFloat(r.creditDebtor || 0),
              )
            : 0}
        </>
      ),
    },
  ];

  // ── Export Excel ──────────────────────────────────────
  const handleExportExcel = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const fromDate = startDate ? dayjs(startDate).format("DD/MM/YYYY") : "";
    const toDate = endDate ? dayjs(endDate).format("DD/MM/YYYY") : "";

    // ✅ ตรงตาม columns
    const rows = filteredData.map((r, i) => ({
      ลำดับ: i + 1,
      สาขา: "MIT",
      วันที่ขาย: r.SDATE
        ? dayjs(r.SDATE).add(543, "year").format("DD/MM/YYYY")
        : "",
      เลขสัญญา: r.CONTNO || "",
      เลขสัญญาใหม่: r.newContno || "",
      สถานะ:
        r.flagLoanNew === 1 && r.LAW_TYPE_ID !== 6
          ? "ทำยอม"
          : r.flagLoanNew === 1 && r.LAW_TYPE_ID === 6
            ? "ทำยอม(ฟ้องส่วนต่าง)"
            : r.flagLoanNew === 2 && r.LAW_TYPE_ID === 6
              ? "พิพากษา(ฟ้องส่วนต่าง)"
              : "พิพากษา",
      ชื่อลูกค้า: `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim(),
      เลขบัตร: r.CUSCOD || "",
      เลขตัวถัง: r.STRNO || "",
      ยี่ห้อ: r.TYPE || "",
      รุ่น: r.MODEL || "",
      ปีผลิต: r.MANUYR || "",
      ทะเบียน: r.REGNO || "",
      เลขเครื่อง: r.ENGNO || "",
      วันทำสัญญา: r.SDATE
        ? dayjs(r.SDATE).add(543, "year").format("DD/MM/YYYY")
        : "",
      งวดแรก: dayjs(r.FDATE).add(543, "year").format("DD/MM/YYYY"),
      งวดสุดท้าย: r.LDATE
        ? dayjs(r.LDATE).add(543, "year").format("DD/MM/YYYY")
        : "-",
      จำนวนงวด: r.T_NOPAY ?? "1",
      // ✅ เปลี่ยนจาก currencyFormatPoint → number เฉพาะ column เงิน
      งวดละ:
        r.flagLoanNew === 1
          ? parseFloat(r.TOT_UPAY) || 0
          : parseFloat(r.totalJugde) || 0,
      ยอดที่ต้องชำระ: parseFloat(r.totalJugde),
      เปอร์เซ็นยอดขาย: parseFloat(r.creditPercen) || 0,
      ลูกหนี้:
        r.flagLoanNew === 1
          ? parseFloat(r.totalJugde)
          : parseFloat(r.creditDebtor),
      ต้นทุน: parseFloat(r.creditDebtor) || 0,
      ดอกผล:
        r.flagLoanNew === 1
          ? (parseFloat(r.totalJugde) || 0) - parseFloat(r.creditDebtor || 0)
          : 0,
    }));

    const moneyKeys = ["งวดละ", "ยอดที่ต้องชำระ", "ลูกหนี้", "ต้นทุน", "ดอกผล"];
    const summaryRow = {
      ลำดับ: "",
      สาขา: `รวมทั้งหมด ${filteredData.length} รายการ`,
    };
    moneyKeys.forEach((key) => {
      summaryRow[key] = rows.reduce((sum, r) => sum + (r[key] || 0), 0);
    });

    const allRows = [...rows, summaryRow];
    const ws = XLSX.utils.json_to_sheet(allRows);
    const keys = Object.keys(rows[0]);

    const evenFill = { patternType: "solid", fgColor: { rgb: "EBF5FF" } };
    const oddFill = { patternType: "solid", fgColor: { rgb: "FFFFFF" } };
    const summaryFill = { patternType: "solid", fgColor: { rgb: "D5E8D4" } };
    const borderStyle = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    rows.forEach((row, ri) => {
      const fill = ri % 2 === 0 ? evenFill : oddFill;
      keys.forEach((key, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };

        const isMoney = moneyKeys.includes(key);
        const isPercent = key === "เปอร์เซ็นยอดขาย" || key === "เปอร์เซ็นปิด";

        ws[cellRef].s = {
          fill,
          font: { sz: 11 },
          alignment: {
            horizontal: typeof row[key] === "number" ? "right" : "left",
            vertical: "center",
          },
          border: borderStyle,
          numFmt: isMoney ? "#,##0.00" : isPercent ? '0.00"%"' : undefined, // ✅
        };
      });
    });

    // summary row
    const summaryRowIdx = rows.length + 1;
    keys.forEach((key, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: summaryRowIdx, c: ci });
      if (!ws[cellRef]) return;

      const isMoney = moneyKeys.includes(key);

      ws[cellRef].s = {
        fill: summaryFill,
        font: { bold: true, sz: 12 },
        alignment: {
          horizontal: isMoney ? "right" : "left",
          vertical: "center",
        },
        border: borderStyle,
        numFmt: isMoney ? "#,##0.00" : undefined, // ✅
      };
    });

    ws["!cols"] = keys.map((key) => ({ wch: Math.max(key.length * 2, 14) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(wb, `รายงานการขาย(ขายสด,ผ่อน)_${fromDate}_${toDate}.xlsx`);
    message.success("Export Excel สำเร็จ");
  };

  // ── Export PDF ────────────────────────────────────────
  const handlePrintPDF = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = dayjs().format("DD/MM/YYYY HH:mm:ss");
    const fromDate = startDate ? dayjs(startDate).format("DD/MM/YYYY") : "";
    const toDate = endDate ? dayjs(endDate).format("DD/MM/YYYY") : "";

    const drawHeader = (pageNum) => {
      doc.setFont("THSarabunNew", "bold");
      doc.setFontSize(16);
      doc.text("รายงานการขาย (ขายสด,ผ่อน)", pageWidth / 2, 8, {
        align: "center",
      });
      doc.text(
        `ขายจากวันที่  ${fromDate}   ถึงวันที่  ${toDate}`,
        pageWidth / 2,
        14,
        { align: "center" },
      );
      doc.setFont("THSarabunNew", "normal");
      doc.setFontSize(14);
      doc.text("บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)", 8, 18);
      doc.text(
        "89 หมู่ 14 ถ.มิตรภาพ ต.ในเมือง อ.เมืองขอนแก่น จ.ขอนแก่น 40000",
        8,
        22,
      );
      doc.setFontSize(14);
      doc.text(`วันที่พิมพ์ ${now}`, 8, 28);
      doc.text(`หน้าที่ ${pageNum}`, pageWidth - 8, 28, { align: "right" });
    };

    const statusLabel = (r) =>
      r.flagLoanNew === 1 && r.LAW_TYPE_ID !== 6
        ? "ทำยอม"
        : r.flagLoanNew === 1 && r.LAW_TYPE_ID === 6
          ? "ทำยอม(ฟ้องส่วนต่าง)"
          : r.flagLoanNew === 2 && r.LAW_TYPE_ID === 6
            ? "พิพากษา(ฟ้องส่วนต่าง)"
            : "พิพากษา";

    // ✅ ตรงตาม columns
    const tableBody = filteredData.map((r, index) => [
      index + 1, // ลำดับ
      "MIT", // สาขา
      r.SDATE ? dayjs(r.SDATE).add(543, "year").format("DD/MM/YY") : "-", // วันที่ขาย
      `${r.newContno ?? ""}\n(${statusLabel(r)})`, // เลขสัญญา (สถานะ)
      `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim(), // ชื่อลูกค้า
      r.STRNO || "-", // เลขตัวถัง
      r.MANUYR || "-", // ปีผลิต
      r.REGNO || "-", // ทะเบียน
      `${r.flagLoanNew === 1 ? currencyFormatPoint(r.TOT_UPAY ?? 0) : currencyFormatPoint(r.totalJugde ?? 0)}\n${r.T_NOPAY ?? "1"}`, //งวดละ
      `${currencyFormatPoint(r.totalJugde ?? 0)}\n${r.T_NOPAY ?? "1"}`, // ยอดที่ต้องชำระ / เปอร์เซ็น
      `${r.flagLoanNew === 1 ? currencyFormatPoint(r.totalJugde ?? 0) : currencyFormatPoint(r.creditDebtor ?? 0)}`, //งวดละ
      `${currencyFormatPoint(r.creditDebtor ?? 0)}\n${r.creditPercen ?? 0}%`, // ต้นทุน / เปอร์เซ็น
      currencyFormatPoint(
        r.flagLoanNew === 1 ? parseFloat(r.totalJugde - r.creditDebtor) : 0,
      ), // ส่วนต่าง
    ]);

    const sumAllRows = (rows) =>
      rows.reduce(
        (acc, r) => ({
          totUpay:
            acc.totUpay +
            (r.flagLoanNew === 1
              ? parseFloat(r.TOT_UPAY) || 0
              : parseFloat(r.totalJugde) || 0),
          totalMustPay: acc.totalMustPay + parseFloat(r.totalJugde) || 0,
          totalDebtor:
            acc.totalDebtor +
            (r.flagLoanNew === 1
              ? parseFloat(r.totalJugde) || 0
              : parseFloat(r.creditDebtor) || 0), // ✅
          creditDebtor: acc.creditDebtor + (parseFloat(r.creditDebtor) || 0),
          interest:
            acc.interest +
            (r.flagLoanNew === 1
              ? (parseFloat(r.totalJugde) || 0) -
                (parseFloat(r.creditDebtor) || 0)
              : 0),
        }),
        {
          totUpay: 0,
          totalMustPay: 0,
          totalDebtor: 0,
          creditDebtor: 0,
          interest: 0,
        },
      );
    const t = sumAllRows(filteredData);

    const makeSummaryRow = (
      label,
      count,
      s,
      textColor = [0, 0, 0],
      fillColor = [236, 240, 241],
    ) => {
      const c = (v) => ({
        content: v,
        styles: {
          halign: "right",
          fontStyle: "bold",
          font: "THSarabunNew",
          fillColor,
          textColor,
        },
      });
      return [
        {
          content: `${label}  ${count} รายการ`,
          colSpan: 8,
          styles: {
            fontStyle: "bold",
            font: "THSarabunNew",
            fillColor,
            textColor,
          },
        },
        c(currencyFormatPoint(s.totUpay)), // col 8:  งวดละ
        c(currencyFormatPoint(s.totalMustPay)), // col 9:  ยอดที่ต้องชำระ
        c(currencyFormatPoint(s.totalDebtor)), // col 10: ลูกหนี้ ✅
        c(currencyFormatPoint(s.creditDebtor)), // col 11: ต้นทุน
        c(currencyFormatPoint(s.interest)), // col 12: ดอกผล
      ];
    };
    doc.autoTable({
      startY: 30,
      head: [
        [
          "ลำดับ",
          "สาขา",
          "วันที่ขาย",
          "เลขสัญญา\n(สถานะ)",
          "ชื่อลูกค้า",
          "เลขตัวถัง",
          "ปีผลิต",
          "ทะเบียน",
          "งวดละ\nจำนวนงวด",
          "ยอดที่ต้องชำระ\nเปอร์เซ็น",
          "ลูกหนี้",
          "ต้นทุน",
          "ดอกผล",
        ],
      ],
      body: [
        ...tableBody,
        makeSummaryRow("รวมทั้งหมด", filteredData.length, t),
      ],
      styles: {
        font: "THSarabunNew",
        fontSize: 14,
        cellPadding: 1,
        halign: "center",
        fillColor: [255, 255, 255],
      },
      headStyles: {
        font: "THSarabunNew",
        fillColor: [52, 73, 94],
        textColor: 255,
        halign: "center",
        fontStyle: "bold",
        fontSize: 14,
      },
      columnStyles: {
        8: { halign: "right" },
        9: { halign: "right" },
        10: { halign: "right" },
        11: { halign: "right" },
        12: { halign: "right" },
      },
      alternateRowStyles: { fillColor: [235, 245, 255] },
      didDrawPage: () =>
        drawHeader(doc.internal.getCurrentPageInfo().pageNumber),
      margin: { top: 30, left: 3, right: 3 },
    });

    doc.save(`รายงานการขาย(ขายสด,ผ่อน)_${fromDate}_${toDate}.pdf`);
  };
  // ✅ Dropdown menu เลือก PDF หรือ Excel
  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      if (key === "excel") handleExportExcel();
    },
  };

  const hasDate = startDate && endDate;
  const filteredData = useMemo(() => handleFilterData(), [handleFilterData]);

  return (
    <>
      <style>{`
      .row-even td { background-color: #f5f8ff !important; }
      .row-odd td  { background-color: #ffffff !important; }
    `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Row>
          <Col span={16}>
            <Space style={{ marginBottom: 10 }}>
              เริ่ม
              <ThaiDatePicker
                size="large"
                value={startDate}
                onChange={setStartDate}
                placeholder={"วันที่เริ่มต้น"}
                allowClear={false}
              />
              ถึง
              <ThaiDatePicker
                size="large"
                value={endDate}
                onChange={setEndDate}
                placeholder={"วันที่สิ้นสุด"}
                allowClear={false}
              />
              <Button size="large" type="primary" onClick={onQuery}>
                ค้นหา
              </Button>
              <Input
                size="small"
                allowClear
                placeholder="ค้นหาเลขสัญญา"
                value={searchContno}
                onChange={(e) => setSearchContno(e.target.value)}
                style={{ width: 200 }}
              />
              <Checkbox
                checked={isFlagAgreement}
                onChange={(e) => setIsFlagAgreement(e.target.checked)}
              >
                ทำยอม
              </Checkbox>
              <Checkbox
                checked={isFlagEnforce}
                onChange={(e) => setIsFlagEnforce(e.target.checked)}
              >
                พิพากษา
              </Checkbox>
            </Space>
          </Col>
          <Col span={8} style={{ textAlign: "end" }}>
            <Space style={{ marginBottom: 10 }}>
              {/* ✅ Dropdown เลือก PDF / Excel */}
              <Dropdown menu={exportMenuItems} disabled={!filteredData.length}>
                <Button size="large" icon={<PrinterOutlined />}>
                  พิมพ์ / Export <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>

        <Card style={{ marginBottom: 10 }}>
          <Divider style={{ fontSize: 20 }}>รายงานการขาย (ขายสด,ผ่อน)</Divider>

          <Typography.Text
            strong
            style={{ display: "block", textAlign: "center", fontSize: 16 }}
          >
            บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)
          </Typography.Text>

          {hasDate && (
            <Typography.Text
              style={{ display: "block", textAlign: "center", fontSize: 16 }}
            >
              ขายจากวันที่ {dayjs(startDate).format("DD/MM/YYYY")} ถึงวันที่{" "}
              {dayjs(endDate).format("DD/MM/YYYY")}
            </Typography.Text>
          )}

          <Table
            style={{ marginTop: 10 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey={(r) => r.idJudge ?? r.CONTNO}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "row-even" : "row-odd"
            } // ✅ ใส่ตรงนี้
            scroll={{ x: 1400 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            summary={() => {
              const t = filteredData.reduce(
                (acc, r) => ({
                  totUpay:
                    acc.totUpay +
                    (r.flagLoanNew === 1
                      ? parseFloat(r.TOT_UPAY) || 0
                      : parseFloat(r.totalJugde) || 0),
                  totalMustPay:
                    acc.totalMustPay + (parseFloat(r.totalJugde) || 0),
                  totalDebtor:
                    acc.totalDebtor +
                    (r.flagLoanNew === 1
                      ? parseFloat(r.totalJugde) || 0
                      : parseFloat(r.creditDebtor) || 0),
                  creditDebtor:
                    acc.creditDebtor + (parseFloat(r.creditDebtor) || 0),
                  interest:
                    acc.interest +
                    (r.flagLoanNew === 1
                      ? (parseFloat(r.totalJugde) || 0) -
                        (parseFloat(r.creditDebtor) || 0)
                      : 0),
                }),
                {
                  totUpay: 0,
                  totalMustPay: 0,
                  totalDebtor: 0,
                  creditDebtor: 0,
                  interest: 0,
                },
              );

              const cs = { fontWeight: "bold", background: "#ecf0f1" };
              const cr = {
                fontWeight: "bold",
                background: "#ecf0f1",
                textAlign: "right",
              };

              return (
                <Table.Summary.Row style={cs}>
                  <Table.Summary.Cell colSpan={8} align="left" style={cs}>
                    รวมทั้งหมด {filteredData.length} รายการ
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" style={cr}>
                    {fmtPos(t.totUpay)}
                  </Table.Summary.Cell>{" "}
                  {/* งวดละ */}
                  <Table.Summary.Cell align="right" style={cr}>
                    {fmtPos(t.totalMustPay)}
                  </Table.Summary.Cell>{" "}
                  {/* ยอดที่ต้องชำระ */}
                  <Table.Summary.Cell align="right" style={cr}>
                    {fmtPos(t.totalDebtor)}
                  </Table.Summary.Cell>{" "}
                  {/* ลูกหนี้ */}
                  <Table.Summary.Cell align="right" style={cr}>
                    {fmtPos(t.creditDebtor)}
                  </Table.Summary.Cell>{" "}
                  {/* ต้นทุน */}
                  <Table.Summary.Cell align="right" style={cr}>
                    {fmtPos(t.interest)}
                  </Table.Summary.Cell>{" "}
                  {/* ดอกผล */}
                </Table.Summary.Row>
              );
            }}
          />
        </Card>
      </Spin>
    </>
  );
};

const MainReportSellingAndInstallments = MotionHoc(Main);
export default MainReportSellingAndInstallments;
